import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticAHU } from "./realistic";

/**
 * AHUNode – Air Handling Unit visualization.
 * Supports:
 * - Realistic 3D SCADA design with rotating supply fan & moving dial needles
 * - Glassmorphism design
 * - Classic industrial SVG layout
 */
const AHUNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticAHU
              liveValue={liveValue}
              unit={unit || "°C"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const strokeColor = isRunning ? color : "#94a3b8";
        const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const spinDuration = isRunning ? `${1.2 / (speedScale || 1)}s` : "0s";

        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="76"
            viewBox="0 0 80 76"
            fill="none"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="ahuGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="ahuActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                  </linearGradient>
                  <filter id="ahuGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* AHU housing */}
                <rect
                  x="5"
                  y="5"
                  width="70"
                  height="50"
                  rx="6"
                  fill="url(#ahuGlassGrad)"
                  stroke={isRunning ? "url(#ahuActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  style={{
                    filter: isRunning ? "url(#ahuGlow)" : "none",
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Pulsing internal airflow glow */}
                {isRunning && (
                  <rect x="8" y="8" width="64" height="44" rx="4" fill={color} opacity="0.12">
                    <animate attributeName="opacity" values="0.05;0.25;0.05" dur="1.5s" repeatCount="indefinite" />
                  </rect>
                )}

                {/* AHU label */}
                <text x="15" y="19" fontSize="9" fontWeight="900" fill={color} style={{ letterSpacing: 0.5 }}>AHU</text>

                {/* Large fan blades (enhanced thickness and visibility) */}
                <g style={{ transformOrigin: "45px 33px", animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none" }}>
                  <ellipse cx="45" cy="20" rx="6" ry="14" fill={isRunning ? "url(#ahuActiveGrad)" : "#64748b"} opacity="0.75" stroke={strokeColor} strokeWidth="1.5" />
                  <ellipse cx="45" cy="46" rx="6" ry="14" fill={isRunning ? "url(#ahuActiveGrad)" : "#64748b"} opacity="0.75" stroke={strokeColor} strokeWidth="1.5" />
                  <ellipse cx="32" cy="33" rx="14" ry="6" fill={isRunning ? "url(#ahuActiveGrad)" : "#64748b"} opacity="0.75" stroke={strokeColor} strokeWidth="1.5" />
                  <ellipse cx="58" cy="33" rx="14" ry="6" fill={isRunning ? "url(#ahuActiveGrad)" : "#64748b"} opacity="0.75" stroke={strokeColor} strokeWidth="1.5" />
                </g>
                <circle cx="45" cy="33" r="6" fill={isRunning ? "url(#ahuActiveGrad)" : "#64748b"} />

                {/* High-visibility Airflow Waves exiting AHU */}
                {isRunning && (
                  <g filter="url(#ahuGlow)">
                    <path d="M 68,26 C 74,23 74,37 84,34" stroke={color} strokeWidth="2.5" fill="none" strokeDasharray="5 5">
                      <animate attributeName="stroke-dashoffset" values="10;0" dur="0.8s" repeatCount="indefinite" />
                    </path>
                    <path d="M 68,40 C 74,37 74,51 84,48" stroke={color} strokeWidth="2" fill="none" strokeDasharray="5 5">
                      <animate attributeName="stroke-dashoffset" values="10;0" dur="1s" repeatCount="indefinite" />
                    </path>
                  </g>
                )}

                {/* Premium status dot */}
                <circle cx="70" cy="10" r="5.5" fill={dotColor} stroke="#ffffff" strokeWidth="2" filter={isRunning ? "url(#ahuGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
                <text x="40" y="70" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
                  {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* AHU housing */}
                <rect x="5" y="5" width="70" height="50" rx="4" fill={`${color}10`} stroke={strokeColor} strokeWidth="2" />

                {/* AHU label */}
                <text x="15" y="18" fontSize="9" fontWeight="bold" fill={color} opacity="0.5">AHU</text>

                {/* Large fan blades */}
                <g style={{ transformOrigin: "45px 33px", animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none" }}>
                  <ellipse cx="45" cy="20" rx="4" ry="12" fill={`${color}30`} stroke={color} strokeWidth="1" />
                  <ellipse cx="45" cy="46" rx="4" ry="12" fill={`${color}30`} stroke={color} strokeWidth="1" />
                  <ellipse cx="32" cy="33" rx="12" ry="4" fill={`${color}30`} stroke={color} strokeWidth="1" />
                  <ellipse cx="58" cy="33" rx="12" ry="4" fill={`${color}30`} stroke={color} strokeWidth="1" />
                </g>
                <circle cx="45" cy="33" r="4" fill={isRunning ? color : "#94a3b8"} />

                {/* Airflow lines */}
                {isRunning && (
                  <g opacity="0.4" style={{ animation: "live-ahu-airflow 2s linear infinite" }}>
                    <path d="M75 25 Q82 30 75 35" stroke={color} strokeWidth="1.5" fill="none" />
                    <path d="M78 25 Q85 30 78 35" stroke={color} strokeWidth="1" fill="none" />
                  </g>
                )}

                {/* Inlet/outlet */}
                <rect x="0" y="28" width="5" height="10" fill={color} rx="2" />
                <rect x="75" y="28" width="5" height="10" fill={color} rx="2" />

                {/* Filter indicator */}
                <rect x="10" y="42" width="20" height="4" rx="1" fill={`${color}25`} stroke={color} strokeWidth="0.5" />

                {/* Status */}
                <circle cx="70" cy="10" r="4" fill={dotColor} />
                <text x="40" y="70" textAnchor="middle" fontSize="7" fontWeight="600" fill={dotColor}>
                  {statusColor === "green" ? "RUNNING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

AHUNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default AHUNode;
